import React, { useCallback, useState } from "react"

import { SelectionChip } from "@coval/ui"
import { GripVertical } from "lucide-react"

interface ReorderableChip {
  id: string
  label: React.ReactNode
  /** Accessible name for the remove control; falls back to the chip's id. */
  removeLabel?: string
  /** An id whose record is missing reads as an error rather than a selection. */
  invalid?: boolean
  /** Spread onto the chip, for the view-context attributes Sofia reads. */
  attributes?: Record<string, string | undefined>
  onRemove?: () => void
}

interface ReorderableChipsProps {
  chips: ReorderableChip[]
  /**
   * Called with the source and destination indices on drop. Omit to render a
   * plain, non-draggable chip row.
   */
  onReorder?: (from: number, to: number) => void
  /**
   * Suppresses dragging while an update is in flight, so two quick drops can't
   * race. Reordering is also pointless below two chips.
   */
  disabled?: boolean
}

/**
 * A row of selection chips that can be dragged into a different order.
 *
 * Ordering is meaningful wherever it is shown — metrics appear to reviewers in
 * this sequence — so the drag affordance lives with the chips rather than in a
 * separate editing mode.
 */
export const ReorderableChips: React.FC<ReorderableChipsProps> = ({
  chips,
  onReorder,
  disabled = false,
}) => {
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const canReorder = Boolean(onReorder) && chips.length > 1 && !disabled

  const handleDragStart = useCallback(
    (event: React.DragEvent<HTMLDivElement>, index: number) => {
      event.dataTransfer.effectAllowed = "move"
      // Firefox refuses to start a drag whose event carries no data.
      event.dataTransfer.setData("text/plain", String(index))
      setDragIndex(index)
    },
    [],
  )

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>, index: number) => {
      event.preventDefault()
      event.dataTransfer.dropEffect = "move"
      setDragOverIndex((previous) => (previous === index ? previous : index))
    },
    [],
  )

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>, index: number) => {
      event.preventDefault()
      if (dragIndex !== null && dragIndex !== index) {
        onReorder?.(dragIndex, index)
      }
      setDragIndex(null)
      setDragOverIndex(null)
    },
    [dragIndex, onReorder],
  )

  const handleDragEnd = useCallback(() => {
    setDragIndex(null)
    setDragOverIndex(null)
  }, [])

  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((chip, index) => {
        const isDragging = dragIndex === index
        const isDropTarget =
          dragOverIndex === index && dragIndex !== null && dragIndex !== index

        return (
          <div
            key={chip.id}
            draggable={canReorder}
            onDragStart={(event) => handleDragStart(event, index)}
            onDragOver={(event) => handleDragOver(event, index)}
            onDrop={(event) => handleDrop(event, index)}
            onDragEnd={handleDragEnd}
            className={`rounded-sm transition-opacity ${
              isDragging ? "opacity-50" : ""
            } ${isDropTarget ? "ring-2 ring-coval-border-active" : ""}`}
          >
            <SelectionChip
              leading={
                canReorder ? (
                  <span
                    className="cursor-grab text-coval-text-muted active:cursor-grabbing"
                    aria-hidden="true"
                  >
                    <GripVertical className="size-3.5" />
                  </span>
                ) : undefined
              }
              label={chip.label}
              invalid={chip.invalid}
              removeLabel={chip.removeLabel}
              onRemove={chip.onRemove}
              {...chip.attributes}
            />
          </div>
        )
      })}
    </div>
  )
}
